import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from '../styles/chats.module.css';
import ld from '../images/load.gif';

function Chats() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { users: currentUser } = useSelector((state) => state);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://chatting-club-backend.onrender.com/api/chats/get-chats', {
        withCredentials: true
      });

      if (response.data.success) {
        setChats(response.data.chats);
      } else {
        toast.error(response.data.message || 'Failed to fetch chats', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    } finally {
      setLoading(false);
    }
  };

  const openChat = (chatId) => {
    navigate(`/chat/${chatId}`);
  };

  const getOtherUser = (chat) => {
    if (!chat.users || !currentUser?.userdata?._id) return null;
    return chat.users.find(
      user => user._id !== currentUser.userdata._id
    );
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRandomColor = (name) => {
    const colors = [
      '#9F7BFF', '#FF6B9D', '#4ADE80', '#60A5FA',
      '#FBBF24', '#F472B6', '#34D399', '#A78BFA',
      '#FB923C', '#22D3EE', '#F87171', '#818CF8'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const messageDate = new Date(date);
    const diff = now - messageDate;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    if (diff < 604800000) return Math.floor(diff / 86400000) + 'd ago';
    return messageDate.toLocaleDateString();
  };

  const getLastMessageText = (chat) => {
    if (chat.messages && chat.messages.length > 0) {
      const messageCount = chat.messages.length;
      return `${messageCount} ${messageCount === 1 ? 'message' : 'messages'}`;
    }
    return 'No messages yet';
  };

  const getLastMessageTime = (chat) => {
    return chat.updatedAt;
  };

  return (
    <div className={styles.chatsContainer}>
      <div className={styles.chatsWrapper}>
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>💬 Chats</h1>
          <p className={styles.pageSubtitle}>
            {chats.length} {chats.length === 1 ? 'chat' : 'chats'}
          </p>
        </div>

        {loading ? (
          <div className={styles.loadingContainer}>
            <img src={ld} className={styles.loaderImage} alt="Loading chats..." />
            <p className={styles.loadingText}>Loading your chats...</p>
          </div>
        ) : chats.length > 0 ? (
          <div className={styles.chatsList}>
            {chats.map((chat) => {
              const otherUser = getOtherUser(chat);
              if (!otherUser) return null;
              
              const lastMessageText = getLastMessageText(chat);
              const lastMessageTime = getLastMessageTime(chat);
              
              return (
                <div
                  key={chat._id}
                  className={styles.chatItem}
                  onClick={() => openChat(chat._id)}
                >
                  <div className={styles.chatItemContent}>
                    <div className={styles.avatarWrapper}>
                      <div 
                        className={styles.userAvatar} 
                        style={{ backgroundColor: getRandomColor(otherUser.name) }}
                      >
                        {getInitials(otherUser.name)}
                      </div>
                    </div>
                    
                    <div className={styles.chatInfo}>
                      <div className={styles.chatHeader}>
                        <h3 className={styles.userName}>{otherUser.name}</h3>
                        <span className={styles.chatTime}>
                          {formatTime(lastMessageTime)}
                        </span>
                      </div>
                      <p className={styles.lastMessage}>
                        {lastMessageText}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <h3 className={styles.emptyTitle}>No Chats Yet</h3>
            <p className={styles.emptyText}>Start chatting with your friends by finding them in the users page.</p>
            <button 
              className={styles.findUsersBtn}
              onClick={() => navigate('/users')}
            >
              Find Users
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Chats;